import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Spinner, Tooltip, OverlayTrigger } from 'react-bootstrap'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateTime, formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import StockItemInfo from '../components/StockItemInfo'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'

function AmazonFBAInventoryDetailScreen() {
    const { reportId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [report, setReport] = useState(null)
    const [reportLoading, setReportLoading] = useState(true)
    const [error, setError] = useState('')

    const [snapshotData, setSnapshotData] = useState([])
    const [snapshotSummary, setSnapshotSummary] = useState(null)
    const [snapshotLoading, setSnapshotLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const initialTableState = {
        visibility: {
            image: true, sku: true, fnsku: true, asin: true,
            productName: true, condition: true, yourPrice: true,
            afnFulfillable: true, afnWarehouse: true, afnUnsellable: true,
            afnReserved: true, afnTotal: true,
            inboundWorking: true, inboundShipped: true, inboundReceiving: true,
            researching: true,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_fba_inventory', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        image: 'Image', sku: 'SKU', fnsku: 'FNSKU', asin: 'ASIN',
        productName: 'Product Name', condition: 'Condition', yourPrice: 'Your Price',
        afnFulfillable: 'Fulfillable', afnWarehouse: 'Warehouse', afnUnsellable: 'Unsellable',
        afnReserved: 'Reserved', afnTotal: 'Total Qty',
        inboundWorking: 'Inbound Working', inboundShipped: 'Inbound Shipped',
        inboundReceiving: 'Inbound Receiving', researching: 'Researching',
    }

    const tipHeader = (label, tip) => {
        const TipHeader = () => (
            <OverlayTrigger placement="top" overlay={<Tooltip>{tip}</Tooltip>}>
                <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>{label}</span>
            </OverlayTrigger>
        )
        TipHeader.displayName = `TipHeader_${label}`
        return TipHeader
    }

    useEffect(() => {
        const fetchReport = async () => {
            setReportLoading(true)
            try {
                const data = await amazonAPI.inventoryReports.getById(reportId)
                setReport(data)
            } catch (err) {
                setError(formatErrorResponse(err))
            } finally {
                setReportLoading(false)
            }
        }
        fetchReport()
    }, [reportId])

    useEffect(() => {
        if (!report) return
        const fetchSummary = async () => {
            try {
                const summary = await amazonAPI.inventorySnapshots.getSummary({ report_request_id: reportId })
                setSnapshotSummary(summary || null)
            } catch (err) {
                console.error('Failed to fetch snapshot summary:', err)
            }
        }
        fetchSummary()
    }, [report, reportId])

    const fetchSnapshotData = useCallback(async () => {
        if (!report) return
        setSnapshotLoading(true)
        setError('')
        try {
            const params = {
                report_request_id: reportId,
                page: currentPage,
                pageSize: pageSize,
            }
            if (searchTerm) params.searchTerm = searchTerm
            if (sortColumn) {
                params.sortColumn = sortColumn
                params.sortDirection = sortDirection || 'asc'
            }

            const data = await amazonAPI.inventorySnapshots.getAll(params)
            if (data && data.results !== undefined) {
                setSnapshotData(data.results)
                setTotalItems(data.count)
            } else {
                setSnapshotData(data || [])
                setTotalItems((data || []).length)
            }
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setSnapshotLoading(false) }
    }, [report, reportId, currentPage, pageSize, searchTerm, sortColumn, sortDirection])

    useEffect(() => { fetchSnapshotData() }, [fetchSnapshotData])

    const handleSearch = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => { setSearchTerm(value); setCurrentPage(1) }, 300)
    }
    const handleClearSearch = () => { setSearchInputValue(''); setSearchTerm(''); setCurrentPage(1) }

    const handleSort = (column, direction) => { setSortColumn(column); setSortDirection(direction); setCurrentPage(1) }

    const snapshotColumns = [
        columnVisibility.image && { id: 'image', header: 'Image', cell: (row) => row.stock_item_detail?.image ? (
            <OverlayTrigger placement="right" overlay={<Tooltip className="stock-item-tooltip"><StockItemInfo stockItem={row.stock_item_detail} /></Tooltip>}>
                <img src={row.stock_item_detail.image} alt="Stock Item Thumbnail" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </OverlayTrigger>
        ) : null },
        columnVisibility.sku && { id: 'sku', header: tipHeader('SKU', 'Amazon Seller SKU (Merchant SKU)'), sortable: true, cell: (row) => row.sku },
        columnVisibility.fnsku && { id: 'fnsku', header: tipHeader('FNSKU', 'Fulfillment Network SKU — Amazon\'s internal barcode for FBA items'), cell: (row) => row.fnsku ? <CopyableText value={row.fnsku} toastLabel="FNSKU" searchTerm={searchTerm} /> : '' },
        columnVisibility.asin && { id: 'asin', header: tipHeader('ASIN', 'Amazon Standard Identification Number'), cell: (row) => row.asin ? <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> : '' },
        columnVisibility.productName && { id: 'productName', header: tipHeader('Product Name', 'Product title as listed on Amazon'), sortable: true, cell: (row) => row.product_name || '' },
        columnVisibility.condition && { id: 'condition', header: tipHeader('Condition', 'Item condition (e.g., New, Used, Refurbished)'), cell: (row) => row.condition || '' },
        columnVisibility.yourPrice && { id: 'yourPrice', header: tipHeader('Your Price', 'Current listing price on Amazon'), sortable: true, cell: (row) => row.your_price != null ? formatCurrency(row.your_price) : '' },
        columnVisibility.afnFulfillable && { id: 'afnFulfillable', header: tipHeader('Fulfillable', 'Units available for customer orders — can be picked, packed, and shipped'), sortable: true, cell: (row) => row.afn_fulfillable_quantity },
        columnVisibility.afnWarehouse && { id: 'afnWarehouse', header: tipHeader('Warehouse', 'Total units at fulfillment centers (fulfillable + reserved + unsellable)'), sortable: true, cell: (row) => row.afn_warehouse_quantity },
        columnVisibility.afnUnsellable && { id: 'afnUnsellable', header: tipHeader('Unsellable', 'Units that cannot be sold (damaged, defective, or expired)'), sortable: true, cell: (row) => row.afn_unsellable_quantity },
        columnVisibility.afnReserved && { id: 'afnReserved', header: tipHeader('Reserved', 'Units set aside — being picked for orders, in FC-to-FC transfer, or pending processing'), sortable: true, cell: (row) => row.afn_reserved_quantity },
        columnVisibility.afnTotal && { id: 'afnTotal', header: tipHeader('Total Qty', 'All units across all statuses (warehouse + inbound + researching)'), sortable: true, cell: (row) => row.afn_total_quantity },
        columnVisibility.inboundWorking && { id: 'inboundWorking', header: tipHeader('Inbound Working', 'Units in shipments created but not yet shipped to Amazon'), sortable: true, cell: (row) => row.afn_inbound_working_quantity },
        columnVisibility.inboundShipped && { id: 'inboundShipped', header: tipHeader('Inbound Shipped', 'Units in transit to fulfillment centers (shipped, not yet received)'), sortable: true, cell: (row) => row.afn_inbound_shipped_quantity },
        columnVisibility.inboundReceiving && { id: 'inboundReceiving', header: tipHeader('Inbound Receiving', 'Units arrived at a fulfillment center, being checked in and processed'), sortable: true, cell: (row) => row.afn_inbound_receiving_quantity },
        columnVisibility.researching && { id: 'researching', header: tipHeader('Researching', 'Misplaced or warehouse-damaged units being investigated (resolved within 60 days)'), sortable: true, cell: (row) => row.afn_researching_quantity },
    ].filter(Boolean)

    if (reportLoading) {
        return (
            <Container fluid className="px-4 py-4">
                <div className="text-center py-4"><Spinner animation="border" /></div>
            </Container>
        )
    }

    const headerSubtitle = report
        ? `${report.seller_account_name || ''} (${formatDateTime(report.requested_at)})`
        : ''

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">FBA Inventory</h1>
                    {headerSubtitle && <p className="text-muted mb-0">{headerSubtitle}</p>}
                </div>
                <Button variant="outline-primary" onClick={() => navigate('/amazon-fba-inventory', { state: { returnFilters } })}>
                    <i className="fas fa-arrow-left me-2"></i>Back to List
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {snapshotLoading && snapshotData.length === 0 ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
                <>
                    {snapshotSummary && (
                        <Row className="mb-4 g-3">
                            <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Total SKUs</h5><h3 className="mb-0">{snapshotSummary.total_skus}</h3></div></Col>
                            <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Total FBA Units</h5><h3 className="mb-0">{(snapshotSummary.total_fba || 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Fulfillable</h5><h3 className="mb-0">{(snapshotSummary.fulfillable || 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-warning text-white p-3 rounded text-center"><h5 className="mb-1">Reserved</h5><h3 className="mb-0">{(snapshotSummary.reserved || 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-secondary text-white p-3 rounded text-center"><h5 className="mb-1">Inbound Shipped</h5><h3 className="mb-0">{(snapshotSummary.inbound_shipped || 0).toLocaleString()}</h3></div></Col>
                        </Row>
                    )}
                    <div className="d-flex align-items-center mb-3">
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search SKU, ASIN, or Name..." />
                        </div>
                    </div>
                    {snapshotData.length === 0 ? (
                        <p className="text-muted">No inventory data found.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={snapshotColumns} data={snapshotData} tableId="amazon_fba_inventory"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        sku: 150, fnsku: 130, asin: 130, productName: 250, condition: 100,
                                        yourPrice: 100, afnFulfillable: 100, afnWarehouse: 100, afnUnsellable: 100,
                                        afnReserved: 100, afnTotal: 100, inboundWorking: 120, inboundShipped: 120,
                                        inboundReceiving: 130, researching: 110,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default AmazonFBAInventoryDetailScreen
